import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import {
  Search,
  Eye,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Stethoscope,
  Users,
  Copy,
  DollarSign,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import { httpClient } from '@/api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { cn } from '@/utils/cn';

interface Withdrawal {
  id: string;
  type: 'DOCTOR' | 'AFFILIATE';
  recipientType: string;
  doctorId?: string;
  doctorName: string;
  doctorEmail: string;
  doctorCrm?: string;
  amount: number;
  pixKey: string;
  pixKeyType: string;
  bankName?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PROCESSING' | 'PAID';
  requestedAt: string;
  processedAt?: string;
  rejectedReason?: string;
}

interface WithdrawalsResponse {
  withdrawals: Withdrawal[];
  total: number;
  page: number;
  pageSize: number;
  stats: {
    pendingCount: number;
    pendingAmount: number;
    approvedCount: number;
    paidAmount: number;
  };
}

export function AdminWithdrawalsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED'>('ALL');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'DOCTOR' | 'AFFILIATE'>('ALL');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [rejectingItem, setRejectingItem] = useState<Withdrawal | null>(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const { data, isLoading, isError, isFetching, refetch } = useQuery<WithdrawalsResponse>({
    queryKey: ['admin', 'withdrawals', page, filter, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: '15',
      });
      if (filter !== 'ALL') params.append('status', filter);
      if (typeFilter !== 'ALL') params.append('type', typeFilter);

      const json = await httpClient.get<{
        data: any[];
        meta: { page: number; perPage: number; total: number };
      }>(`/admin/financial/payouts?${params}`);

      const withdrawals: Withdrawal[] = json.data.map((p: any) => ({
        id: p.id,
        type: p.type || (p.wallet ? 'DOCTOR' : 'AFFILIATE'),
        recipientType: p.recipientType || (p.type === 'AFFILIATE' ? 'Indique e Ganhe' : 'Médico'),
        doctorId: p.doctorId,
        doctorName: p.recipientName || p.doctorName || 'Usuário',
        doctorEmail: p.recipientEmail || p.doctorEmail || 'N/A',
        doctorCrm: p.doctorCrm || 'N/A',
        amount: Number(p.amount) || 0,
        pixKey: p.pixKey || 'N/A',
        pixKeyType: p.pixKeyType || 'CPF',
        status: p.status === 'APPROVED' || p.status === 'PAID' ? 'PAID' : p.status === 'PENDING' ? 'PENDING' : 'REJECTED',
        requestedAt: p.createdAt,
        processedAt: p.processedAt,
        rejectedReason: p.rejectionReason,
      }));

      const pendingItems = withdrawals.filter((w) => w.status === 'PENDING');
      const paidItems = withdrawals.filter((w) => w.status === 'PAID' || w.status === 'APPROVED');

      return {
        withdrawals,
        total: json.meta.total,
        page: json.meta.page,
        pageSize: json.meta.perPage,
        stats: {
          pendingCount: pendingItems.length,
          pendingAmount: pendingItems.reduce((acc, curr) => acc + curr.amount, 0),
          approvedCount: paidItems.length,
          paidAmount: paidItems.reduce((acc, curr) => acc + curr.amount, 0),
        },
      };
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (w: Withdrawal) => {
      const endpoint = w.type === 'AFFILIATE'
        ? `/admin/affiliate-payouts/${w.id}/approve`
        : `/admin/payouts/${w.id}/approve`;
      return httpClient.post(endpoint, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'withdrawals'] });
      setSelectedWithdrawal(null);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ w, reason }: { w: Withdrawal; reason: string }) => {
      const endpoint = w.type === 'AFFILIATE'
        ? `/admin/affiliate-payouts/${w.id}/reject`
        : `/admin/payouts/${w.id}/reject`;
      return httpClient.post(endpoint, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'withdrawals'] });
      setRejectingItem(null);
      setRejectionReasonInput('');
      setSelectedWithdrawal(null);
    },
  });

  const filteredWithdrawals = data?.withdrawals.filter((w) => {
    const matchesSearch =
      w.doctorName.toLowerCase().includes(search.toLowerCase()) ||
      w.doctorEmail.toLowerCase().includes(search.toLowerCase()) ||
      w.pixKey.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  }) || [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  const getStatusBadge = (status: Withdrawal['status']) => {
    switch (status) {
      case 'PENDING':
        return {
          bg: 'bg-amber-100 dark:bg-amber-950/40 border-amber-300',
          text: 'text-amber-800 dark:text-amber-300',
          icon: Clock,
          label: 'Em Análise',
        };
      case 'APPROVED':
      case 'PAID':
        return {
          bg: 'bg-emerald-100 dark:bg-emerald-950/40 border-emerald-300',
          text: 'text-emerald-800 dark:text-emerald-300',
          icon: Check,
          label: 'Aprovado / Pago',
        };
      case 'REJECTED':
        return {
          bg: 'bg-rose-100 dark:bg-rose-950/40 border-rose-300',
          text: 'text-rose-800 dark:text-rose-300',
          icon: XCircle,
          label: 'Recusado',
        };
      case 'PROCESSING':
        return {
          bg: 'bg-purple-100 dark:bg-purple-950/40 border-purple-300',
          text: 'text-purple-800 dark:text-purple-300',
          icon: AlertCircle,
          label: 'Processando',
        };
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Gerenciamento de Saques (Pix Out)</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Aprovação e liquidação de repasses médicos e bonificações de afiliados Indique e Ganhe
          </p>
        </div>

        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold rounded-xl border border-border bg-card hover:bg-muted text-foreground transition-all shadow-xs cursor-pointer disabled:opacity-50 self-start sm:self-auto shrink-0"
          title="Atualizar lista de solicitações"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin text-primary')} />
          <span>{isFetching ? 'Atualizando...' : 'Atualizar Dados'}</span>
        </button>
      </div>

      {/* 4 Cards de Estatísticas com Métricas Reais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="rounded-2xl border border-border shadow-xs bg-card">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-3 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-2xl border border-amber-500/20">
              <Clock className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Saques em Análise</p>
              <p className="text-2xl font-black text-amber-600 mt-0.5">{data?.stats.pendingCount || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-border shadow-xs bg-card">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-3 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-2xl border border-blue-500/20">
              <DollarSign className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Valor em Análise</p>
              <p className="text-2xl font-black text-foreground mt-0.5">{formatCurrency(data?.stats.pendingAmount || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-border shadow-xs bg-card">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl border border-emerald-500/20">
              <CheckCircle className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Aprovados</p>
              <p className="text-2xl font-black text-emerald-600 mt-0.5">{data?.stats.approvedCount || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-border shadow-xs bg-card">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-3 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-2xl border border-purple-500/20">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Volume Total Pago</p>
              <p className="text-2xl font-black text-purple-600 mt-0.5">{formatCurrency(data?.stats.paidAmount || 0)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por nome, email ou chave PIX..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-border rounded-xl text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary shadow-xs"
          />
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Filtro por Origem (Médicos vs Afiliados) */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="px-3.5 py-2.5 border border-border rounded-xl text-sm font-semibold bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary shadow-xs cursor-pointer"
          >
            <option value="ALL">Todas as Origens</option>
            <option value="DOCTOR">Médicos (Honorários)</option>
            <option value="AFFILIATE">Afiliados (Indique e Ganhe)</option>
          </select>

          {/* Filtro por Status */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3.5 py-2.5 border border-border rounded-xl text-sm font-semibold bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary shadow-xs cursor-pointer"
          >
            <option value="ALL">Todos os Status</option>
            <option value="PENDING">Em Análise (Pendentes)</option>
            <option value="APPROVED">Aprovados / Pagos</option>
            <option value="REJECTED">Recusados</option>
          </select>
        </div>
      </div>

      {/* Tabela de Saques */}
      <Card className="rounded-2xl border border-border shadow-xs bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-muted/40 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3.5">Favorecido / Destinatário</th>
                <th className="px-4 py-3.5">Origem</th>
                <th className="px-4 py-3.5">Chave PIX</th>
                <th className="px-4 py-3.5">Valor Solicitado</th>
                <th className="px-4 py-3.5">Solicitado em</th>
                <th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <LoadingOverlay message="Carregando solicitações de saque..." />
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan={7} className="p-8">
                    <ErrorRetry message="Erro ao carregar solicitações de saque" onRetry={() => refetch()} />
                  </td>
                </tr>
              ) : filteredWithdrawals.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center">
                    <EmptyState
                      icon="Clock"
                      title="Nenhuma solicitação encontrada"
                      description={search || filter !== 'ALL' || typeFilter !== 'ALL' ? 'Tente ajustar os filtros de busca' : 'Não há solicitações de saque no momento'}
                      action={{ label: 'Atualizar', onClick: () => refetch() }}
                    />
                  </td>
                </tr>
              ) : (
                filteredWithdrawals.map((w) => {
                  const statusBadge = getStatusBadge(w.status);
                  const StatusIcon = statusBadge.icon;
                  const isPending = w.status === 'PENDING';

                  return (
                    <tr key={w.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className={cn(
                              'w-10 h-10 rounded-2xl flex items-center justify-center font-black text-xs shrink-0 text-white',
                              w.type === 'AFFILIATE'
                                ? 'bg-gradient-to-tr from-purple-600 to-indigo-600'
                                : 'bg-gradient-to-tr from-blue-600 to-primary'
                            )}
                          >
                            {w.doctorName.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground text-sm truncate">{w.doctorName}</p>
                            <p className="text-xs text-muted-foreground truncate">{w.doctorEmail}</p>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3.5">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold border',
                            w.type === 'AFFILIATE'
                              ? 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/20'
                              : 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20'
                          )}
                        >
                          {w.type === 'AFFILIATE' ? <Users className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                          {w.type === 'AFFILIATE' ? 'Indique e Ganhe' : 'Médico'}
                        </span>
                      </td>

                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs font-semibold text-foreground bg-muted/40 px-2 py-0.5 rounded-md border border-border">
                            {w.pixKeyType}: {w.pixKey}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(w.pixKey);
                              setCopiedKey(w.id);
                              setTimeout(() => setCopiedKey(null), 2000);
                            }}
                            className="p-1 text-muted-foreground hover:text-foreground rounded hover:bg-muted transition-colors cursor-pointer"
                            title="Copiar Chave PIX"
                          >
                            {copiedKey === w.id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </td>

                      <td className="px-4 py-3.5 font-black text-emerald-600 text-sm">
                        {formatCurrency(w.amount)}
                      </td>

                      <td className="px-4 py-3.5 text-xs text-muted-foreground">
                        {formatDate(w.requestedAt)}
                      </td>

                      <td className="px-4 py-3.5">
                        <div className="space-y-0.5">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-bold border',
                              statusBadge.bg,
                              statusBadge.text
                            )}
                          >
                            <StatusIcon className="h-3.5 w-3.5" />
                            {statusBadge.label}
                          </span>
                          {w.rejectedReason && (
                            <p className="text-[11px] text-rose-600 dark:text-rose-400 truncate max-w-[180px]">
                              {w.rejectedReason}
                            </p>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedWithdrawal(w)}
                            className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg border border-transparent hover:border-blue-200 transition-colors cursor-pointer"
                            title="Ver Detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </button>

                          {isPending && (
                            <>
                              <button
                                onClick={() => approveMutation.mutate(w)}
                                disabled={approveMutation.isPending}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs transition-colors cursor-pointer disabled:opacity-50"
                                title="Aprovar e disparar transferência PIX OUT"
                              >
                                <Check className="h-3.5 w-3.5" />
                                <span>Aprovar Pix</span>
                              </button>

                              <button
                                onClick={() => {
                                  setRejectingItem(w);
                                  setRejectionReasonInput('Chave PIX ou dados bancários divergentes');
                                }}
                                disabled={rejectMutation.isPending}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors cursor-pointer disabled:opacity-50"
                                title="Recusar solicitação e estornar saldo"
                              >
                                <X className="h-3.5 w-3.5" />
                                <span>Recusar</span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
          <p className="text-xs text-muted-foreground font-medium">
            Mostrando {((page - 1) * 15) + 1} a {Math.min(page * 15, data?.total || 0)} de {data?.total || 0} solicitações
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 border border-border rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Página Anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 py-1 bg-primary text-primary-foreground font-bold text-xs rounded-lg shadow-xs">
              {page}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page * 15 >= (data?.total || 0)}
              className="p-1.5 border border-border rounded-lg hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Próxima Página"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* Modal de Detalhes da Solicitação */}
      <Dialog open={!!selectedWithdrawal} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
          <div className="p-5 bg-gradient-to-r from-blue-600 via-primary to-indigo-600 text-white flex items-center justify-between">
            <DialogTitle className="text-base font-bold text-white">Detalhes do Saque (PIX OUT)</DialogTitle>
            <button
              type="button"
              onClick={() => setSelectedWithdrawal(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {selectedWithdrawal && (
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-0.5">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Origem</span>
                  <p className="font-bold text-foreground text-sm">{selectedWithdrawal.recipientType}</p>
                </div>

                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-0.5">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Valor Solicitado</span>
                  <p className="font-bold text-emerald-600 text-sm">{formatCurrency(selectedWithdrawal.amount)}</p>
                </div>

                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-0.5 col-span-2">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Favorecido</span>
                  <p className="font-bold text-foreground text-sm">{selectedWithdrawal.doctorName}</p>
                  <p className="text-xs text-muted-foreground">{selectedWithdrawal.doctorEmail}</p>
                </div>

                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-0.5 col-span-2">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Chave PIX de Destino</span>
                  <p className="font-mono font-bold text-foreground text-sm">{selectedWithdrawal.pixKeyType}: {selectedWithdrawal.pixKey}</p>
                </div>

                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-0.5">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Data Solicitação</span>
                  <p className="font-semibold text-foreground text-xs">{formatDate(selectedWithdrawal.requestedAt)}</p>
                </div>

                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-0.5">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">Status</span>
                  <p className="font-bold text-foreground text-xs">{selectedWithdrawal.status}</p>
                </div>

                {selectedWithdrawal.rejectedReason && (
                  <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-700 dark:text-rose-300 col-span-2 text-xs">
                    <span className="font-bold block">Motivo da Recusa:</span>
                    <p className="mt-0.5">{selectedWithdrawal.rejectedReason}</p>
                  </div>
                )}
              </div>

              {selectedWithdrawal.status === 'PENDING' && (
                <div className="pt-2 flex items-center justify-end gap-2 border-t border-border">
                  <button
                    type="button"
                    onClick={() => {
                      setRejectingItem(selectedWithdrawal);
                      setRejectionReasonInput('Chave PIX ou dados bancários divergentes');
                    }}
                    className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-rose-300 text-rose-600 hover:bg-rose-50 transition-colors"
                  >
                    Recusar Saque
                  </button>
                  <button
                    type="button"
                    onClick={() => approveMutation.mutate(selectedWithdrawal)}
                    disabled={approveMutation.isPending}
                    className="px-4 py-2 text-xs font-bold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 shadow-xs transition-colors"
                  >
                    {approveMutation.isPending ? 'Processando Pix...' : 'Aprovar e Transferir Pix'}
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Recusa */}
      <Dialog open={!!rejectingItem} onOpenChange={() => setRejectingItem(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border border-border shadow-2xl bg-card">
          <div className="p-5 bg-rose-600 text-white flex items-center justify-between">
            <DialogTitle className="text-base font-bold text-white">Recusar Solicitação de Saque</DialogTitle>
            <button
              type="button"
              onClick={() => setRejectingItem(null)}
              className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {rejectingItem && (
            <div className="p-6 space-y-4">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Ao recusar o saque de <strong className="text-foreground">{formatCurrency(rejectingItem.amount)}</strong> para{' '}
                <strong className="text-foreground">{rejectingItem.doctorName}</strong>, o valor bloqueado retornará automaticamente para o saldo disponível do usuário.
              </p>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                  Motivo da Recusa (visível para o usuário)
                </label>
                <input
                  type="text"
                  placeholder="Ex: Chave PIX não localizada no banco central"
                  value={rejectionReasonInput}
                  onChange={(e) => setRejectionReasonInput(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <DialogFooter className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRejectingItem(null)}
                  className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-border text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => rejectMutation.mutate({ w: rejectingItem, reason: rejectionReasonInput.trim() || 'Chave PIX inválida ou dados bancários divergentes' })}
                  disabled={rejectMutation.isPending}
                  className="px-4 py-2 text-xs font-bold rounded-xl bg-rose-600 text-white hover:bg-rose-700 shadow-xs transition-colors"
                >
                  {rejectMutation.isPending ? 'Recusando...' : 'Confirmar Recusa e Devolver Saldo'}
                </button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
